import React, { useRef, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import {
  X, Upload, Loader2, FileSpreadsheet, CheckCircle2, AlertTriangle,
  Download, ArrowLeft, Wand2, Brain,
} from "lucide-react";
import { toast } from "sonner";

const CANONICAL_LABELS = {
  expediente: "Expediente",
  titulo: "Título / Objeto",
  municipio: "Municipio",
  tipo_servicio: "Tipo de servicio",
  estado_crm: "Estado CRM",
  importe_licitacion: "Importe licitación",
  fecha_presentacion: "Fecha presentación",
  fecha_adjudicacion_estimada: "Fecha adjudicación",
  responsable_comercial: "Responsable comercial",
  notas_comerciales: "Notas",
  competidores: "Competidores",
  ganador: "Ganador",
  presupuesto_adjudicacion: "Presupuesto adjudicación",
};

export default function LicitacionImportModal({ onClose, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(1);    // 1: pick / 2: review / 3: result
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dedupe, setDedupe] = useState(true);
  const [result, setResult] = useState(null);
  const [learning, setLearning] = useState(false);
  const [learnResult, setLearnResult] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xlsm)$/i.test(f.name)) {
      toast.error("Sólo se admiten archivos .xlsx o .xlsm");
      return;
    }
    setFile(f);
    setPreview(null);
    setResult(null);
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/licitaciones-import/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      setMapping(r.data.mapping || {});
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error procesando Excel");
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/licitaciones-import/template.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "licitaciones_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error descargando plantilla"); }
  };

  const confirmImport = async () => {
    if (!preview) return;
    if (!mapping.titulo && !mapping.expediente) {
      toast.error("Debes mapear al menos 'Título' o 'Expediente'");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("dedupe", String(dedupe));
      const r = await axios.post(`${API}/licitaciones-import/confirm-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      setStep(3);
      toast.success(`✓ ${r.data.inserted} licitaciones importadas`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error confirmando import");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="licit-import-modal"
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Upload size={16} className="text-[#007934]"/> Importar histórico de licitaciones
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Sube tu Excel macro (.xlsm) o .xlsx. La Pando detecta el mapeo de columnas y tú confirmas.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>

        {/* Stepper */}
        <div className="px-5 py-2 border-b border-gray-100 flex items-center gap-2 text-[11px] font-semibold text-gray-500">
          {["1. Subir", "2. Revisar mapeo", "3. Resultado"].map((label, idx) => {
            const active = step === (idx + 1);
            const done = step > (idx + 1);
            return (
              <div key={label} className="flex items-center gap-1">
                <span className={`px-2 py-0.5 rounded-full ${
                  active ? "bg-[#007934] text-white" :
                  done ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                }`}>{label}</span>
                {idx < 2 && <span className="text-gray-300">›</span>}
              </div>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {step === 1 && (
            <div className="space-y-4" data-testid="licit-import-step1">
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-gray-200 hover:border-[#007934] rounded-xl p-8 text-center cursor-pointer transition-colors">
                <FileSpreadsheet size={36} className="text-gray-300 mx-auto mb-3"/>
                {file ? (
                  <>
                    <p className="text-sm font-semibold text-gray-800">{file.name}</p>
                    <p className="text-[11px] text-gray-500 mt-1">{(file.size/1024).toFixed(0)} KB</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-gray-700">Selecciona tu Excel histórico</p>
                    <p className="text-[11px] text-gray-500 mt-1">.xlsx o .xlsm (Excel macro)</p>
                  </>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  onChange={onPick}
                  className="hidden"
                  data-testid="licit-import-file"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-[11px] text-blue-900 flex items-start gap-2">
                <Brain size={14} className="mt-0.5 shrink-0"/>
                <div>
                  La Pando (Claude Sonnet 4.5) analizará las cabeceras de tu Excel y propondrá
                  un mapeo automático. Podrás ajustarlo antes de confirmar.
                  <button
                    type="button"
                    onClick={downloadTemplate}
                    data-testid="licit-import-template"
                    className="ml-2 text-blue-700 hover:underline inline-flex items-center gap-1 font-semibold">
                    <Download size={11}/> Descargar plantilla guía
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={runPreview}
                  disabled={!file || busy}
                  data-testid="licit-import-analyze"
                  className="bg-[#007934] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#006528] disabled:opacity-50 inline-flex items-center gap-2">
                  {busy ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>}
                  Analizar con Pando
                </button>
              </div>
            </div>
          )}

          {step === 2 && preview && (
            <div className="space-y-4" data-testid="licit-import-step2">
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <Stat label="Hoja" value={preview.sheet}/>
                <Stat label="Filas detectadas" value={preview.row_count_total}/>
                <Stat label="Mapeos sugeridos" value={Object.keys(mapping).length}/>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-semibold text-gray-700 flex items-center justify-between">
                  <span>Mapeo de columnas Excel → CRM</span>
                  <span className="text-[10px] text-gray-500">
                    {Object.keys(preview.ia_mapping || {}).length > 0 ? "✓ Pando" : "Heurístico"}
                  </span>
                </div>
                <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                  {Object.entries(CANONICAL_LABELS).map(([canonical, label]) => {
                    const required = canonical === "titulo" || canonical === "expediente";
                    const currentHeader = mapping[canonical] || "";
                    return (
                      <div key={canonical} className="flex items-center gap-3 px-3 py-2 text-[11px]">
                        <div className="w-44 shrink-0">
                          <div className="font-semibold text-gray-800">{label}</div>
                          <div className="text-[10px] text-gray-400 font-mono">{canonical}</div>
                        </div>
                        <span className="text-gray-300">←</span>
                        <select
                          value={currentHeader}
                          onChange={(e) => {
                            const v = e.target.value;
                            setMapping(prev => {
                              const next = { ...prev };
                              if (v) next[canonical] = v;
                              else delete next[canonical];
                              return next;
                            });
                          }}
                          data-testid={`licit-import-map-${canonical}`}
                          className={`flex-1 border rounded px-2 py-1 ${
                            currentHeader
                              ? "border-emerald-300 bg-emerald-50/40"
                              : required ? "border-amber-300 bg-amber-50/40" : "border-gray-200"
                          }`}>
                          <option value="">— sin mapear —</option>
                          {(preview.headers || []).map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                        {required && !currentHeader && (
                          <span className="text-[10px] text-amber-700 font-semibold">obligatorio</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-semibold text-gray-700">
                  Preview de filas mapeadas ({Math.min(preview.preview_rows?.length || 0, 5)} de {preview.row_count_total})
                </div>
                <div className="overflow-x-auto">
                  <table className="text-[11px] w-full">
                    <thead className="bg-gray-50 text-gray-500 uppercase text-[9px]">
                      <tr>
                        {Object.keys(mapping).map(k => (
                          <th key={k} className="text-left p-2 whitespace-nowrap">{CANONICAL_LABELS[k] || k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(preview.preview_rows || []).slice(0, 5).map((row, i) => (
                        <tr key={i}>
                          {Object.keys(mapping).map(k => (
                            <td key={k} className="p-2 text-gray-700 align-top max-w-[200px] truncate" title={String(row[k] ?? "")}>
                              {Array.isArray(row[k]) ? row[k].join(", ") : (row[k] ?? "—")}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {!preview.preview_rows?.length && (
                        <tr><td className="p-3 text-center text-gray-400">Sin filas para mostrar</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <label className="flex items-center gap-2 text-[11px] text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dedupe}
                  onChange={(e) => setDedupe(e.target.checked)}
                  className="accent-[#007934]"
                  data-testid="licit-import-dedupe"
                />
                Detectar duplicados (no insertar si ya existe expediente+municipio en el CRM)
              </label>
            </div>
          )}

          {step === 3 && result && (
            <div className="space-y-4" data-testid="licit-import-step3">
              <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-300 rounded-xl p-5 text-center">
                <CheckCircle2 size={36} className="mx-auto text-emerald-600 mb-2"/>
                <p className="text-2xl font-bold text-emerald-700">{result.inserted}</p>
                <p className="text-sm text-emerald-800 font-semibold">licitaciones insertadas en el CRM</p>
              </div>

              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <Stat label="Total filas" value={result.total_rows}/>
                <Stat label="Duplicados omitidos" value={result.skipped_dupe} cls="text-amber-700"/>
                <Stat label="Filas vacías" value={result.skipped_empty} cls="text-gray-500"/>
              </div>

              {/* Aprender de mi histórico — RAG agregado por municipio */}
              <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-300 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Brain size={22} className="text-violet-600 shrink-0 mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-violet-900">Aprender de mi histórico</p>
                    <p className="text-[11px] text-violet-700 mt-0.5">
                      Construye un índice agregado por municipio (ganador típico, importe medio,
                      competidores recurrentes) y enriquece automáticamente el chat Pando de cada
                      nueva licitación con esos datos.
                      {result.auto_indexed && (
                        <span className="block mt-1 font-semibold">
                          ✓ Indexado automáticamente: {result.auto_indexed.municipios_indexados} municipios ·
                          {" "}{result.auto_indexed.total_licitaciones_procesadas} licitaciones procesadas.
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      setLearning(true);
                      try {
                        const r = await axios.post(`${API}/licitaciones-import/learn`);
                        setLearnResult(r.data);
                        toast.success(`✓ ${r.data.municipios_indexados} municipios indexados`);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Error indexando");
                      } finally { setLearning(false); }
                    }}
                    disabled={learning}
                    data-testid="licit-import-learn"
                    className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-60 inline-flex items-center gap-1.5 shrink-0">
                    {learning ? <Loader2 size={12} className="animate-spin"/> : <Brain size={12}/>}
                    {result.auto_indexed ? "Re-indexar manualmente" : "Aprender de mi histórico"}
                  </button>
                </div>
                {learnResult && !result.auto_indexed && (
                  <div className="mt-3 bg-white rounded-lg p-2.5 text-[11px] text-violet-900 font-semibold border border-violet-200">
                    ✓ {learnResult.municipios_indexados} municipios indexados ·
                    {" "}{learnResult.total_licitaciones_procesadas} licitaciones procesadas.
                  </div>
                )}
              </div>

              {result.errors?.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                  <p className="text-[11px] font-bold text-rose-700 mb-2 flex items-center gap-1">
                    <AlertTriangle size={12}/> Errores ({result.errors.length})
                  </p>
                  <ul className="text-[11px] text-rose-700 space-y-1 max-h-32 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <li key={i}>Fila {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-2">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
              <ArrowLeft size={11}/> Volver
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
              {step === 3 ? "Cerrar" : "Cancelar"}
            </button>
            {step === 2 && (
              <button
                onClick={confirmImport}
                disabled={busy}
                data-testid="licit-import-confirm"
                className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#006528] disabled:opacity-50 inline-flex items-center gap-1.5">
                {busy ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}
                Importar {preview?.row_count_preview || 0} filas
              </button>
            )}
            {step === 3 && (
              <button
                onClick={() => { onImported(); }}
                className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#006528]">
                Ver licitaciones
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, cls = "" }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
      <div className="text-[9px] uppercase text-gray-500 font-bold tracking-wide">{label}</div>
      <div className={`text-lg font-bold ${cls || "text-gray-900"}`}>{value ?? 0}</div>
    </div>
  );
}
